import type { IdvSendTiming } from '@common/enums';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CustomPhoneInput from '@/components/CustomPhoneInput';
import LexoSwitch from '@/components/LexoSwitch';
import type { ClientDetailsEntry, ClientDetailsErrors } from './clientEntries';

interface Props {
    title: string;
    client: ClientDetailsEntry;
    /** The firm's default timing, which turning automatic ID verification on picks */
    defaultIdvSendTiming: IdvSendTiming | null;
    errors?: ClientDetailsErrors;
    onChange(patch: Partial<ClientDetailsEntry>): void;
    onRemove?(): void;
}

const IDV_TIMING_OPTIONS: { value: IdvSendTiming; label: string; description: string }[] = [
    {
        value: 'WithIntake',
        label: 'With intake email',
        description: 'Sent when the matter is created',
    },
    {
        value: 'OnCompletion',
        label: 'On intake completion',
        description: 'Sent when the client submits the intake',
    },
];

/** One client in the New Prospect modal: name, email, cell number and automatic ID verification */
export default function ClientCard({
    title,
    client,
    defaultIdvSendTiming,
    errors = {},
    onChange,
    onRemove,
}: Props) {
    const isIdvEnabled = Boolean(client.idvSendTiming);

    return (
        <Stack
            gap={2}
            sx={{
                border: '1px solid',
                borderColor: 'neutral.N_100',
                borderRadius: 2,
                p: 2,
                bgcolor: 'neutral.white',
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                minHeight={30}
            >
                <Typography variant="h4" sx={{ color: 'text.primary' }}>
                    {title}
                </Typography>
                {onRemove && (
                    <Button
                        type="button"
                        color="error"
                        size="small"
                        startIcon={<RemoveIcon fontSize="small" />}
                        onClick={onRemove}
                        sx={{ textTransform: 'none' }}
                    >
                        Remove
                    </Button>
                )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5}>
                <TextField
                    fullWidth
                    required
                    label="First name"
                    value={client.firstName}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    onChange={(event) => onChange({ firstName: event.target.value })}
                />
                <TextField
                    fullWidth
                    label="Middle name"
                    value={client.middleName}
                    onChange={(event) => onChange({ middleName: event.target.value })}
                />
                <TextField
                    fullWidth
                    required
                    label="Last name"
                    value={client.lastName}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    onChange={(event) => onChange({ lastName: event.target.value })}
                />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems="flex-start">
                <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    value={client.contactEmail}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail}
                    onChange={(event) => onChange({ contactEmail: event.target.value })}
                />
                <Box sx={{ width: '100%' }}>
                    <CustomPhoneInput
                        label={isIdvEnabled ? 'Cell number *' : 'Cell number'}
                        isRequired={isIdvEnabled}
                        value={client.phoneNumber}
                        selectedCountryCode={client.phoneCountryCode}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber}
                        onChange={(phoneNumber) => onChange({ phoneNumber })}
                        onCountryChange={({ code }) => onChange({ phoneCountryCode: code })}
                    />
                </Box>
            </Stack>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} minHeight={40}>
                <FormControlLabel
                    sx={{ ml: 0, mr: 0, gap: 1 }}
                    control={
                        <LexoSwitch
                            checked={isIdvEnabled}
                            onChange={(event) =>
                                onChange({
                                    idvSendTiming: event.target.checked
                                        ? (defaultIdvSendTiming ?? 'WithIntake')
                                        : null,
                                })
                            }
                        />
                    }
                    label="Send ID verification automatically"
                />
                {isIdvEnabled && (
                    <TextField
                        select
                        size="small"
                        value={client.idvSendTiming}
                        onChange={(event) =>
                            onChange({ idvSendTiming: event.target.value as IdvSendTiming })
                        }
                        SelectProps={{
                            inputProps: { 'aria-label': 'When to send ID verification' },
                            renderValue: (value) =>
                                IDV_TIMING_OPTIONS.find((option) => option.value === value)?.label,
                        }}
                        sx={{ minWidth: 240 }}
                    >
                        {IDV_TIMING_OPTIONS.map(({ value, label, description }) => (
                            <MenuItem key={value} value={value}>
                                <ListItemText primary={label} secondary={description} />
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {isIdvEnabled && (
                    // Stacked and small, so both fit beside the dropdown in about its height
                    <Stack sx={{ '& .MuiCheckbox-root': { py: 0.25 } }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={client.idvSecondaryIdRequired ?? false}
                                    onChange={(event) =>
                                        onChange({ idvSecondaryIdRequired: event.target.checked })
                                    }
                                />
                            }
                            label="Request second ID"
                            slotProps={{ typography: { variant: 'body2' } }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={client.idvBankingVerificationRequired ?? false}
                                    onChange={(event) =>
                                        onChange({
                                            idvBankingVerificationRequired: event.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Include banking verification"
                            slotProps={{ typography: { variant: 'body2' } }}
                        />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}
